/* global facebook */
import React, { FC, ReactElement, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Action } from 'redux';
import styled from 'styled-components';
import useTranslator from '../../../hooks/useTranslator';
import useNeverShowShopToClub from '../../../hooks/flags/useNeverShowShopToClub';
import { apiRegisterNewUser, apiLogin, apiLoginWithFacebook, unSetRedirectUrl } from '../../../redux/user/actions';
import BaseOverlay from '../GenericPopup';
import { UserData, RegisterUserValues, ValidationErrors, SessionTypes } from '../../../redux/user/types';
import StateInterface from '../../../redux/types';
import { hideOverlay, showOverlay } from '../../../redux/overlays/actions';
import { useLogger } from '../../../logger/LoggerProvider';
import OverlayLoader from '../../ListingPage/Loader';
import { LoginAndRegisterOverlayOptions } from '../../../redux/overlays/types';
import useConfig from '../../../hooks/useConfig';
import loadFbSdk, { isFbDefined } from '../../ThirdPartyIntegration/FacebookSdk';
import { OverlayId } from '../types';
import useGa from '../../../hooks/useGa';
import Header from './Header';
import Form from './Form';
import Footer from './Footer';

export const OVERLAY_ID = OverlayId.LOGIN_AND_REGISTER;
const FACEBOOK_STATUS_CONNECTED = 'connected';
export const REGISTRATION_SOURCE = 'registration_overlay';
export const REGISTRATION_PLATFORM = 'mobile';

const getValidationErrors = (loginError: ValidationErrors | Error) => {
    if (!(loginError instanceof Error) && !Array.isArray(loginError)) {
        return {
            email: loginError.email,
            password: loginError.password,
        };
    }
    return {};
};

interface LoginStateProps {
    loginStatus: boolean;
    userLoginData: UserData | undefined;
    loginErrors: ValidationErrors | Error;
    isFetching: boolean;
    isRegisterForm: boolean;
    isRegisteredWithNewsletter: boolean;
    redirectUrl?: string;
    overlays: {};
}

interface OwnProps {
    login: (...args: Parameters<typeof apiLogin>) => void;
    registerNewUser: (...args: Parameters<typeof apiRegisterNewUser>) => void;
    loginWithFacebook: (...args: Parameters<typeof apiLoginWithFacebook>) => void;
    hideNavigationFlyout?: (...args: Parameters<typeof hideOverlay>) => void;
    unsetRedirect: (...args: Parameters<typeof unSetRedirectUrl>) => void;
    toggleIsLoginForm: (isLogin: boolean) => void;
}

const ContentWrapper = styled.div`
    padding: 0 10px 20px;
    background: ${({ theme }) => theme.colors.white};
`;

type LoginAndRegisterProps = OwnProps & LoginStateProps;

// eslint-disable-next-line complexity
export const LoginAndRegister: FC<LoginAndRegisterProps> = ({
    registerNewUser,
    login,
    loginWithFacebook,
    userLoginData,
    loginErrors,
    isFetching,
    redirectUrl,
    toggleIsLoginForm,
    isRegisteredWithNewsletter,
    isRegisterForm,
    hideNavigationFlyout = () => {},
    unsetRedirect,
    overlays,
}): ReactElement => {
    const gaTracker = useGa();
    const logger = useLogger();
    const config = useConfig();
    const isActive = overlays[OVERLAY_ID] && overlays[OVERLAY_ID].isActive;
    const [isFacebookLoaded, setIsFacebookLoaded] = useState(isFbDefined());
    const t = useTranslator();
    const [, setNeverShowShopToClub] = useNeverShowShopToClub();

    const handleToggleFormButtonClick = () => {
        toggleIsLoginForm(!isRegisterForm);
    };

    const validationErrors = getValidationErrors(loginErrors);

    const handleFormSubmit = (formValues: RegisterUserValues) => {
        if (isRegisterForm) {
            registerNewUser({
                registrationSource: REGISTRATION_SOURCE,
                registrationPlatform: REGISTRATION_PLATFORM,
                ...formValues,
            });
        } else {
            login(formValues.email, formValues.password);
        }
        setNeverShowShopToClub();
    };

    const statusChangeCallback = (response: facebook.StatusResponse) => {
        try {
            if (response.status === FACEBOOK_STATUS_CONNECTED) {
                loginWithFacebook(response.authResponse.accessToken);
            } else {
                // eslint-disable-next-line no-undef
                FB.login((loginResponse: facebook.StatusResponse) => {
                    if (loginResponse.status !== FACEBOOK_STATUS_CONNECTED) {
                        loginWithFacebook(loginResponse.authResponse.accessToken);
                    }
                });
            }
        } catch (e) {
            logger.error('Facebook login failed.', { error: e });
        }
    };

    const handleFacebookLogin = () => {
        try {
            // eslint-disable-next-line no-undef
            FB.getLoginStatus((response: facebook.StatusResponse) => {
                statusChangeCallback(response);
            });
        } catch (e) {
            statusChangeCallback(e);
        }
    };

    useEffect(() => {
        if (isRegisteredWithNewsletter && isActive) {
            gaTracker.loginAndRegister.trackSuccessfulRegistration();
        }
    }, [gaTracker.loginAndRegister, isActive, isRegisteredWithNewsletter]);

    useEffect(() => {
        if (isActive && !isFbDefined()) {
            loadFbSdk(config.facebookAppId, () => setIsFacebookLoaded(true));
        }

        if (!isActive) {
            unsetRedirect();
        }
    }, [isActive, config.facebookAppId, unsetRedirect]);

    const sessionType = userLoginData && userLoginData.sessionType;
    useEffect(() => {
        if (sessionType === SessionTypes.full) {
            if (redirectUrl) {
                window.location.pathname = redirectUrl;
            }
            hideNavigationFlyout(OVERLAY_ID);
        }
    }, [hideNavigationFlyout, redirectUrl, sessionType]);

    let partialLoginEmail = '';
    let title = isRegisterForm ? t('New at Westwing?') : t('Already customer?');

    if (userLoginData && userLoginData.sessionType === SessionTypes.partial) {
        const { email, firstName, lastName } = userLoginData;
        partialLoginEmail = email || '';
        title = isRegisterForm ? t('New at Westwing?') : `${t('Hello')}, ${firstName} ${lastName}`;
    }

    return (
        <BaseOverlay identifier={OVERLAY_ID}>
            <ContentWrapper data-testid={isRegisterForm ? 'reg__mode' : 'login__mode'}>
                <OverlayLoader loading={isFetching} />
                <Header
                    title={title}
                    infoText={
                        isRegisterForm
                            ? t('Register now and save your favourite products for later!')
                            : t('Login now and save your favourite products for later!')
                    }
                />
                <Form
                    isRegisterForm={isRegisterForm}
                    handleFormSubmit={handleFormSubmit}
                    handleFacebookLogin={handleFacebookLogin}
                    validationErrors={validationErrors}
                    partialLoginEmail={partialLoginEmail}
                    isFetching={isFetching}
                    isFacebookLoaded={isFacebookLoaded}
                />
                <Footer
                    handleToggleFormButtonClick={handleToggleFormButtonClick}
                    text={isRegisterForm ? t('Already registered?') : t('Are you new?')}
                    buttonText={isRegisterForm ? t('Login now') : t('Register now')}
                />
            </ContentWrapper>
        </BaseOverlay>
    );
};

const mapDispatchToProps = (dispatch: Function) => ({
    login: (username: string, password: string) => {
        dispatch(apiLogin(username, password));
    },
    registerNewUser: (formValues: RegisterUserValues) => {
        dispatch(apiRegisterNewUser(formValues));
    },
    loginWithFacebook: (token: string) => dispatch(apiLoginWithFacebook(token)),
    hideNavigationFlyout: (id: string): Action => dispatch(hideOverlay(id)),
    toggleIsLoginForm: (isLogin: boolean): Action =>
        dispatch(
            showOverlay(OVERLAY_ID, {
                isLogin: !isLogin,
            })
        ),
    unsetRedirect: () => {
        dispatch(unSetRedirectUrl());
    },
});

const mapStateToProps = (state: StateInterface) => {
    const overlays = state.overlays.byIds;
    const options = overlays[OVERLAY_ID] && (overlays[OVERLAY_ID].options as LoginAndRegisterOverlayOptions);
    const isRegisterForm = !(options && options.isLogin);

    return {
        loginStatus: state.login.isLoggedIn,
        userLoginData: state.login.data,
        loginErrors: state.login.error,
        isFetching: state.login.isFetching,
        redirectUrl: state.login.redirectUrl,
        isRegisteredWithNewsletter: state.login.isRegisteredWithNewsletter,
        overlays,
        isRegisterForm,
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(LoginAndRegister);
